
import { RegFormProps } from '../interfaces/Props';
import Config from '../config/config';
import { Observable } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

/**
 * Like rxjs/fetch fromFetch, but does not AbortController.abort() on unsubscribe.
 * Avoids CRA Unhandled Rejection (AbortError) when subscribers tear down mid-flight.
 */
const fromFetch = (input: string | Request, init?: RequestInit): Observable<Response> =>
  new Observable<Response>((subscriber) => {
    let closed = false;
    fetch(input, init)
      .then((response) => {
        if (closed) {
          return;
        }
        subscriber.next(response);
        subscriber.complete();
      })
      .catch((err) => {
        if (!closed) {
          subscriber.error(err);
        }
      });
    return () => {
      closed = true;
    };
  });

const getConfigs = () => fromFetch(
  `${Config.apiPath}/res.config.settings`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain'
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getHandicapRequirement=(token:string,date:string)=> fromFetch(
  `${Config.apiPath}/product.courseprofile.item.handicaprequirement/${date}`,{
    method:'GET',
    headers:{
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': 'en'
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getServerTime = () => fromFetch(
  `${Config.apiPath}/res.config.settings/servertime`, {
    method:'GET',
    headers:{
      'Content-Type': 'text/plain',
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getFieldOptionsTitle = (locale?: string) => fromFetch(
  `${Config.apiPath}/res.partner.title`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getFieldOptionsCountry = (locale?: string) => fromFetch(
  `${Config.apiPath}/res.country`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getFieldOptionsTerritories = (locale?: string) => fromFetch(
  `${Config.apiPath}/res.country.state?filters=${encodeURIComponent('[["country_id.code", "=", "HK"]]')}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getFieldOptionsGolfOrg = (locale?: string) => fromFetch(
  `${Config.apiPath}/res.partner.golforganization`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const login = (username: string, password: string, replaceSession?: boolean) => fromFetch(
  `${Config.apiPath}/auth/get_tokens`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: replaceSession !== undefined ? JSON.stringify({ username, password, replace_session: replaceSession, db: Config.db}) : JSON.stringify({ username, password, db: Config.db}),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const logoutDeleteToken = (token: string) => fromFetch(
  `${Config.apiPath}/auth/delete_tokens`, {
    method: 'POST', 
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ refresh_token: token }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const regGetVerifyCodeEmail = (email: string) => fromFetch(
  `${Config.apiPath}/res.users/verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ email }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const regGetVerifyCodeMobile = (mobile: string) => fromFetch(
  `${Config.apiPath}/res.users/verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ mobile }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const regValidateCodeEmail = (email: string, random_email: string) => fromFetch(
  `${Config.apiPath}/res.users/check_verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ email, random_email }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const regValidateCodeMobile = (mobile: string, random_mobile: string) => fromFetch(
  `${Config.apiPath}/res.users/check_verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ mobile, random_mobile }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const regValidateKSCGolfPass = (kscGolfPass: string) => fromFetch(
  `${Config.apiPath}/res.users/check_golfpass`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ kscgolfpass: { pass_no: kscGolfPass } }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const regValidateHKID = (type: string, hkid: string, child_signup?: boolean) => fromFetch(
  `${Config.apiPath}/res.users/check_idno`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ id_type: type, partner_idno: hkid, child_signup: child_signup || false }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const register = (formData: RegFormProps) => fromFetch(
  `${Config.apiPath}/res.users/signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify( formData ),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const requestResetLink = (email: string) => fromFetch(
  `${Config.apiPath}/res.users/reset_password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ login: email }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const refreshToken = ( token: string ) => fromFetch(
  `${Config.apiPath}/auth/refresh_token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ refresh_token: token }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getBookingToken = (token: string) => fromFetch(
  `${Config.apiPath}/ksc.booking/booking_token`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token' : token,
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getProfile = (token: string, locale?: string) => fromFetch(
  `${Config.apiPath}/res.users/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token' : token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Get update profile email verification code
const profileGetVerifyCodeEmail = ( token: string, email: string ) => fromFetch(
  `${Config.apiPath}/res.users/me/verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      email: email
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Get update profile mobile verification code
const profileGetVerifyCodeMobile = ( token: string, mobile: string ) => fromFetch(
  `${Config.apiPath}/res.users/me/verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      mobile: mobile
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Check update profile email verification code
const profileCkVerifyCodeEmail = ( token: string, email: string, random_email: string ) => fromFetch(
  `${Config.apiPath}/res.users/me/check_verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      email, 
      random_email
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Check update profile mobile verification code
const profileCkVerifyCodeMobile = ( token: string, mobile: string, random_mobile: string ) => fromFetch(
  `${Config.apiPath}/res.users/me/check_verification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      mobile,
      random_mobile
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Update Profile, email only
const profileUpdateEmail = ( token: string, email: string, random_email: string ) => fromFetch(
  `${Config.apiPath}/res.users/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      email: email,
      random_email: random_email
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Update Profile, mobile only
const profileUpdateMobile = ( token: string, mobile: string, random_mobile: string ) => fromFetch(
  `${Config.apiPath}/res.users/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      mobile: mobile,
      random_mobile: random_mobile
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Update Profile, pw only
const profileUpdatePassword = ( token: string, password: string, confirm_password: string ) => fromFetch(
  `${Config.apiPath}/res.users/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      password: password,
      confirm_password: confirm_password
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Update Profile, for any fields other than email, mobile and pw
const profileUpdateFields = ( token: string, fields: any ) => fromFetch(
  `${Config.apiPath}/res.users/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify( fields )
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Check access to check, create, change and cancel booking
const checkBookingAccess = ( token: string, booking_id?: number ) => fromFetch(
  `${Config.apiPath}/ksc.booking/checkaccess`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify(booking_id ? { booking_id } : {}),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Get Bookable Dates from server for selection in create booking
const getBookableDates = ( token: string ) => fromFetch(
  `${Config.apiPath}/res.config.settings/bookabledates`, {
    method: "GET",
    headers: {
      'Content-Type': 'text/plain',
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Get Course type from server for selection in create booking
const getCourseType = ( token: string, locale?: string ) => fromFetch(
  `${Config.apiPath}/product.template?order=${encodeURIComponent('course_code asc')}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token' : token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getBookableCourse = ( token: string, play_date: string ) => fromFetch(
  `${Config.apiPath}/ksc.booking/assign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      play_date: play_date,
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const checkQualifiedBooker = ( token: string, play_date: string, course_id: number, course_type_id: number, desiredteetime: string ) => fromFetch(
  `${Config.apiPath}/ksc.booking/assign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({ play_date, course_id, course_type_id, desiredteetime })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Check whether allow to assign
const checkAllowAssign = ( token: string ) => fromFetch(
  `${Config.apiPath}/ksc.booking/allow_assign`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Assign TeeTime
const searchTeeTime = ( token: string, play_date: string, course_id: number, course_type_id: number, desiredteetime: string, player_count: number, qualified_per_team: number, recaptchaToken: string ) => fromFetch(
  `${Config.apiPath}/ksc.booking/assign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      play_date: play_date,
      course_id: course_id,
      course_type_id: course_type_id,
      desiredteetime: desiredteetime,
      player_count: player_count,
      qualified_per_team: qualified_per_team,
      'g-recaptcha-response': recaptchaToken,
      action: 'assign',
    }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Reject Assigned Tee Time
const rejectAssignedTeeTime = ( token: string, booking_id: number ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/draft`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Get Golfer by Golfer ID
const getGolferByKSCID = ( token: string, ksc_id: string ) => fromFetch(
  `${Config.apiPath}/res.partner/ksc/${ksc_id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Get Player Buddies
const getPlayerBuddies = ( token: string ) => fromFetch(
  `${Config.apiPath}/res.partner/me/buddy`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token  
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Check Player can play in this game
const ckPlayerCanPlay = ( token: string, bk_id: number, player_id: number[] ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${bk_id}/confirm`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      player_ids: player_id
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const confirmBooking = ( token: string, bk_id: number, player_ids: number[] ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${bk_id}/confirm`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      player_ids: player_ids,
      action: 'confirm'
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getUpcomingBookings = ( token: string, filter?: string, locale?: string ) => fromFetch(
  `${Config.apiPath}/ksc.booking/upcoming${filter}`, { 
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getBookingDetail = ( token: string, booking_id: number, locale?: string ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const ckCancelPenalty = ( token: string, booking_id: number ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/checkcancel`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const ckCancelWithError = ( token: string, booking_id: number ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/cancelerror`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const cancelBooking = ( token: string, booking_id: number ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/cancel`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const ckWithDrawPenalty = ( token: string, booking_id: number, player_id?: number ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/checkwithdraw`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify( player_id ? { member: player_id } : {} )
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const withDrawPlayer = ( token: string, booking_id: number, player_id?: number ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/withdraw`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify( player_id ? { member: player_id } : {} )
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const ckWithDrawWithError = ( token: string, booking_id: number, player_id?: number ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/withdrawerror`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify( player_id ? { member: player_id } : {} )
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const cancelOnWithdraw = ( token: string, booking_id: number ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/withdraw`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({ non_booker_cancel: true })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const reassignBooking = ( token: string, booking_id: number, reassignBody: any ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/reassign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify(reassignBody)
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const rejectReassignBooking = ( token: string, booking_id: number) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/cancel_modify`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const confirmReassignBooking = ( token: string, booking_id: number, player_ids: number[] ) => fromFetch(
  `${Config.apiPath}/ksc.booking/${booking_id}/confirm`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      player_ids: player_ids,
      action: 'confirm'
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getBuddyInvite = ( token: string ) => fromFetch(
  `${Config.apiPath}/res.partner.buddyinvitation`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const createBuddyInvite = ( token: string, search_key: string ) => fromFetch(
  `${Config.apiPath}/res.partner.buddyinvitation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({
      search: search_key,
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const acceptBuddyInvite = ( token: string, invite_id: number ) => fromFetch(
  `${Config.apiPath}/res.partner.buddyinvitation/${invite_id}/accept`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const rejectBuddyInvite = ( token: string, invite_id: number ) => fromFetch(
  `${Config.apiPath}/res.partner.buddyinvitation/${invite_id}/reject`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const deleteBuddy = ( token: string, parent_id: number ) => fromFetch(
  `${Config.apiPath}/res.partner/me/buddy/${parent_id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getEntitlements = ( token: string, locale?: string ) => fromFetch(
  `${Config.apiPath}/ksc.ticket/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getChildAcs = ( token: string ) => fromFetch(
  `${Config.apiPath}/res.users/me/children`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const createChildAc = ( token: string, formData: any ) => fromFetch(
  `${Config.apiPath}/res.users/child_signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify(formData)
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getNotices = (token?: string, locale?: string) => fromFetch(
  `${Config.apiPath}/res.config.settings.notice`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token!,
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const resetPWCheckToken = ( token: string ) => fromFetch(
  `${Config.apiPath}/res.users/check_token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ token })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const resetPWChangePW = ( token: string, password: string, confirm_password: string ) => fromFetch(
  `${Config.apiPath}/res.users/change_password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({ token, password, confirm_password })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getActivePenalty = ( token: string ) => fromFetch(
  `${Config.apiPath}/res.partner.penalty/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

/**
 * CRM stamp APIs (Access-Token auth, same as other /api routes):
 * GET  /api/crm.stamp.batch/me
 * GET  /api/crm.stamp.coupon/me
 * POST /api/crm.stamp.coupon/redeem  body: { qrcode }
 */
const getStampBatches = ( token: string, locale?: string ) => fromFetch(
  `${Config.apiPath}/crm.stamp.batch/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getCoupons = ( token: string, locale?: string ) => fromFetch(
  `${Config.apiPath}/crm.stamp.coupon/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getPointLedger = ( token: string, locale?: string ) => fromFetch(
  `${Config.apiPath}/crm.point.ledger/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en'
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const redeemCoupon = ( token: string, qrcode: string ) => fromFetch(
  `${Config.apiPath}/crm.stamp.coupon/redeem`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token
    },
    body: JSON.stringify({ qrcode })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

/** Launch flags: hide Stamp Card / Coupons / Points until CRM launch dates are live */
const getCrmFeatures = ( token: string ) => fromFetch(
  `${Config.apiPath}/crm.features`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
    }
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getOldGolferProfile = (id_type: string, partner_idno: string, dob: string, recaptchaToken: string) => fromFetch(
  `${Config.apiPath}/res.partner/old`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify({
      id_type,
      partner_idno,
      dob,
      'g-recaptcha-response': recaptchaToken,
    })
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// Get Course type from server for selection in create booking
const checkNewVersion = () => fromFetch(
  `/`, {
    method: 'GET',
    cache: 'no-cache',
  })
  .pipe(
    mergeMap(async (res) => {
      if (res.status === 200) {
        const matchPattern = /\/static\/js\/main.*\.chunk\.js/;
        const body = await res.text();
        const newFileArray = body.match(matchPattern);
        if (newFileArray) {
          const newFile = newFileArray[0];
          const getCurrentScriptPath = () => () => (new Error().stack as any).match(/([^ \n])*([a-z]*:\/\/\/?)*?[a-z0-9/\\]*\.js/ig)[0];
          const currentScriptPath: string = getCurrentScriptPath()();
          const currentFileArray = currentScriptPath.match(matchPattern);
          if (currentFileArray) {
            const currentFile = currentFileArray[0];
            return {
              code: res.status,
              newVersion: currentFile !== newFile,
            };
          }
        }
      }
  
      return {
        error: 'CONNECTION-ERROR',
      };
    }),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const formResponse = (res:Response):Promise<any> => {
  return res.json().then( (obj:any) => {
    return {
      code: res.status,
      ...obj
    };
  });
}

const getKscCourses = (
  token: string,
  locale?: string,
  query?: {
    class_id?: number;
    class_type_id?: number;
    rota_id?: number;
    festive_program_id?: number;
    subclass_id?: number;
  }
) => {
  const params: string[] = [];
  if (query && query.class_id != null) {
    params.push('class_id=' + encodeURIComponent(String(query.class_id)));
  }
  if (query && query.class_type_id != null) {
    params.push('class_type_id=' + encodeURIComponent(String(query.class_type_id)));
  }
  if (query && query.rota_id != null) {
    params.push('rota_id=' + encodeURIComponent(String(query.rota_id)));
  }
  if (query && query.festive_program_id != null) {
    params.push(
      'festive_program_id=' + encodeURIComponent(String(query.festive_program_id))
    );
  }
  if (query && query.subclass_id != null) {
    params.push('subclass_id=' + encodeURIComponent(String(query.subclass_id)));
  }
  const qs = params.length ? '?' + params.join('&') : '';
  return fromFetch(
    `${Config.apiPath}/ksc.course${qs}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'text/plain',
        'Access-Token': token,
        'Language': locale === 'tc' ? 'zh_TW' : 'en',
      },
    }).pipe(
      mergeMap((res) => formResponse(res)),
      catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
    );
};

const getKscCourse = (
  token: string,
  courseId: number,
  locale?: string
) => fromFetch(
  `${Config.apiPath}/ksc.course/${courseId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const createKscEnrollment = (
  token: string,
  body: { course_id: number; ksc_ids?: string[]; student_ids?: number[] },
  locale?: string
) => fromFetch(
  `${Config.apiPath}/ksc.course/enrollment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
    body: JSON.stringify(body),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const checkKscEnrollmentAccess = (
  token: string,
  body: { course_id: number; ksc_ids: string[] },
  locale?: string
) => fromFetch(
  `${Config.apiPath}/ksc.course/enrollment/checkaccess`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
    body: JSON.stringify(body),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getKscEnrollmentPayment = (
  token: string,
  enrollmentId: number,
  locale?: string
) => fromFetch(
  `${Config.apiPath}/ksc.course/enrollment/${enrollmentId}/payment`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getEnrolmentProcedures = (token: string, locale?: string) => fromFetch(
  `${Config.apiPath}/ksc.course/enrollment/procedures`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getMyKscEnrollments = (token: string, locale?: string) => fromFetch(
  `${Config.apiPath}/ksc.course/enrollment/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const markKscLessonAttendance = (
  token: string,
  enrollmentId: number,
  partId: number,
  locale?: string
) => fromFetch(
  `${Config.apiPath}/ksc.course/enrollment/${enrollmentId}/attendance`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
    body: JSON.stringify({ part_id: partId }),
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const cancelKscEnrollment = (
  token: string,
  enrollmentId: number,
  locale?: string,
  options?: { studentId?: number; cancelAll?: boolean }
) => {
  const body: { student_id?: number; cancel_all?: boolean } = {};
  if (options && options.studentId != null) {
    body.student_id = options.studentId;
  }
  if (options && options.cancelAll) {
    body.cancel_all = true;
  }
  return fromFetch(
    `${Config.apiPath}/ksc.course/enrollment/${enrollmentId}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        'Access-Token': token,
        'Language': locale === 'tc' ? 'zh_TW' : 'en',
      },
      body: Object.keys(body).length ? JSON.stringify(body) : undefined,
    }).pipe(
      mergeMap((res) => formResponse(res)),
      catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
    );
};

const getNextKscRota = (token: string, locale?: string) => fromFetch(
  `${Config.apiPath}/ksc.rota/next`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getAvailableKscRotas = (token: string, locale?: string) => fromFetch(
  `${Config.apiPath}/ksc.rota/available`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getKscFestivePrograms = (token: string, locale?: string) => fromFetch(
  `${Config.apiPath}/ksc.festive.program`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

const getKscClassTypes = (token: string, locale?: string) => fromFetch(
  `${Config.apiPath}/ksc.class.type`, {
    method: 'GET',
    headers: {
      'Content-Type': 'text/plain',
      'Access-Token': token,
      'Language': locale === 'tc' ? 'zh_TW' : 'en',
    },
  }).pipe(
    mergeMap((res) => formResponse(res)),
    catchError((reason) => Promise.resolve({error: 'CONNECTION-ERROR'}))
  );

// TODO(API 901): Add SES enrolment endpoints when available, e.g.
// getEnrolmentCourses(token) → GET /api/ksc.course?... (available_to_public, app_web, ≥24h)
// getEnrolmentCourseDetail(token, id) → used by Batch B+
// createEnrolment / listMyEnrolments / cancelEnrolment → replace src/mocks/sesMockApi.ts
// saveEnrolmentConsent (T&C + marketing opt_out) → replace sessionStorage consentByCourse
// suspended/unable-to-enrol gate → login/SES API (StudentEnrolment dialog shell already present)

export default {
  getConfigs,
  getHandicapRequirement,
  getServerTime,
  getFieldOptionsTitle,
  getFieldOptionsCountry,
  getFieldOptionsTerritories,
  getFieldOptionsGolfOrg,
  login,
  logoutDeleteToken,
  regGetVerifyCodeEmail,
  regGetVerifyCodeMobile,
  regValidateCodeEmail,
  regValidateCodeMobile,
  regValidateKSCGolfPass,
  regValidateHKID,
  register,
  requestResetLink,
  getBookingToken,
  refreshToken,
  getProfile,
  profileGetVerifyCodeEmail,
  profileGetVerifyCodeMobile,
  profileCkVerifyCodeEmail,
  profileCkVerifyCodeMobile,
  profileUpdateEmail,
  profileUpdateMobile,
  profileUpdatePassword,
  profileUpdateFields,
  checkBookingAccess,
  getBookableDates,
  getCourseType,
  getBookableCourse,
  checkQualifiedBooker,
  checkAllowAssign,
  searchTeeTime,
  rejectAssignedTeeTime,
  getGolferByKSCID,
  getPlayerBuddies,
  ckPlayerCanPlay,
  confirmBooking,
  getUpcomingBookings,
  getBookingDetail,
  ckCancelPenalty,
  ckCancelWithError,
  cancelBooking,
  ckWithDrawPenalty,
  withDrawPlayer,
  reassignBooking,
  rejectReassignBooking,
  confirmReassignBooking,
  getBuddyInvite,
  createBuddyInvite,
  acceptBuddyInvite,
  rejectBuddyInvite,
  deleteBuddy,
  getEntitlements,
  getChildAcs,
  createChildAc,
  getNotices,
  resetPWCheckToken,
  resetPWChangePW,
  getActivePenalty,
  getStampBatches,
  getCoupons,
  getPointLedger,
  redeemCoupon,
  getCrmFeatures,
  getOldGolferProfile,
  ckWithDrawWithError,
  cancelOnWithdraw,
  checkNewVersion,
  getKscClassTypes,
  getKscFestivePrograms,
  getNextKscRota,
  getAvailableKscRotas,
  getKscCourses,
  getKscCourse,
  createKscEnrollment,
  checkKscEnrollmentAccess,
  getKscEnrollmentPayment,
  getEnrolmentProcedures,
  getMyKscEnrollments,
  markKscLessonAttendance,
  cancelKscEnrollment,
}
